<template>
  <div>
    <h4 class="section-title">
      {{title}}
      <span class="section-sub-title">資料來源：經濟部商業司</span>
    </h4>
    <div class="toolbar">
      <div>
        <el-row>
          <el-col style="color:#606266;font-size:13px;text-align:left;line-height:30px">
            地址關聯度
          </el-col>
        </el-row>
        <el-row>
          <el-col>
            <el-select
              size="mini"
              placeholder="地址關聯度"
              v-model="resolutionLevel"
              @change="onResolutionLevelChange">
              <el-option
                v-for="item in resolutionLevelOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value">
              </el-option>
            </el-select>
          </el-col>
        </el-row>
      </div>
      <div>
        <el-row>
          <el-col style="color:#606266;font-size:13px;text-align:left;line-height:30px">
            門牌範圍
          </el-col>
        </el-row>
        <el-row>
          <el-col>
            <el-select
              size="mini"
              placeholder="門牌範圍"
              v-model="selectedAddr"
              multiple
              @change="onSelectedAddrChange">
              <el-option
                v-for="item in selectedAddrOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value">
              </el-option>
            </el-select>
          </el-col>
        </el-row>
      </div>
    </div>
    <div class="collapse">
      <el-collapse v-model="collapseShowList">
        <el-collapse-item name="cls">
          <template #title>
            <font-awesome-icon
              :icon="['fas', 'user-tag']"
              style="margin-right: 0.5rem !important"
            />
            重要人物提及
          </template>
          <div class="detail-card-toolbar-item info">
            <span class="color-danger">{{ companyName }}</span>
            相似董監事：
            <span
              v-for="(item, index) in peopleStatObj.similar_shareholders"
              :key="`ref-shareholder-${index}`"
            >
              <span class="color-danger">{{ item.name }}</span>
              ({{ item.count }}次)
              <span v-if="index < peopleStatObj.similar_shareholders.length - 1">、</span>
            </span>
          </div>
          <div class="detail-card-toolbar-item info">
            非
            <span class="color-danger">{{ companyName }}</span>
            相關，但多次提及人名：
            <span
              v-for="(item, index) in peopleStatObj.people_refered"
              :key="`ref-people-${index}`"
            >
              <span class="color-danger">{{ item.name }}</span>
              ({{ item.count }}次)
              <span v-if="index < peopleStatObj.people_refered.length - 1">、</span>
            </span>
          </div>
        </el-collapse-item>
      </el-collapse>
    </div>
    <div class="table-set">
      <el-table
        :data="currentTableData"
        empty-text="目前暫無資料喔 :)"
        v-loading="isLoading"
        size="large"
        highlight-current-row
        stripe
      >
        <el-table-column label="序號" type="index" align="center" width="56"></el-table-column>
        <el-table-column label="近似公司/營業狀態/登記地址">
          <template #default="scope">
            <div class="info">
              {{ scope.row.company_name }}
              <router-link
                target="_blank"
                :to="returnCompanyLinkTo(scope.row.company_name)">
                <font-awesome-icon :icon="[ 'fas', 'external-link-alt' ]" />
              </router-link>
            </div>
            <div
              :class="
                scope.row.company_operate === '營業中' ? 'color-success' : 'color-danger'
              "
            >
              {{ scope.row.company_operate }}
            </div>
            <div>{{ scope.row.company_address }}</div>
          </template>
        </el-table-column>
        <el-table-column label="稅籍">
          <template #default="scope">
            <div v-for="(item, index) in scope.row.etax_matters_tideup" :key="`etax-${index}`">
              {{ item }}
            </div>
            <div>{{ scope.row.etax_matters_tideup.length ? '' : '-' }}</div>
          </template>
        </el-table-column>
        <el-table-column label="負責人/董監事/經理人">
          <template #default="scope">
            <div>{{ scope.row.representative_name }}</div>
            <div v-for="(item, index) in scope.row.shareholders" :key="`shareholders-${index}`">
              {{ item }}
            </div>
            <div v-for="(item, index) in scope.row.managers" :key="`managers-${index}`">
              {{ item }}
            </div>
            <div>
              {{
                scope.row.representative_name ||
                scope.row.shareholders.length ||
                scope.row.managers.length
                  ? ''
                  : '-'
              }}
            </div>
          </template>
        </el-table-column>
        <el-table-column label="相似董監事成員">
          <template #default="scope">
            <div v-if="scope.row.similar_shareholders.length" class="color-danger">
              {{ companyName }}
            </div>
            <div v-else>{{ '-' }}</div>
            <div
              v-for="(item, index) in scope.row.similar_shareholders"
              :key="`similar_shareholders-${index}`"
            >
              {{ item }}
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>
    <div class="align-right">
      <el-pagination
        layout="prev, pager, next"
        :total="totalCount"
        @current-change="onPageChange">
      </el-pagination>
    </div>
  </div>
</template>

<script>
import { defineComponent, SetupContext, ref, computed, ComputedRef, Ref, watch } from '@vue/composition-api'
// import type {
//   CompanyAddressSimilarObj,
//   CompanyAddressSimilarCls,
//   Stat
// } from '@/types/company'
import * as codes from '@/const/codes'
import * as apis from '@/apis/index.ts'
import { defaultRelevenceParams } from '@/const/defaultRelevenceParams'
import { createDefaultSimilarAddressQuery } from '@/utils/createDefaultSimilarAddressQuery'
// interface Props {
//   addressSimilar: {
//     stat: Stat
//     list: CompanyAddressSimilarObj[]
//     [key: string]: any
//   }
//   companyName: string
//   isLoading: boolean
// }


const resolutionLevelOptions = [
  { label: '完全相同', value: 'match'},
  { label: '號', value: '號'},
  { label: '樓/F', value: '樓'},
  { label: '之/-', value: '之'},
]

export default defineComponent({
  props: {
    title: {
      default: () => codes.COMPANY_DETAIL_ITEM_DICT.登記地址近似公司
    },
    登記地址近似公司: {
      required: true,
      default: () => ({ stat: {}, list: [] })
    },
    companyName: {
      default: () => ''
    },
    // isLoading: {
    //   default: () => false
    // },
    selectable_addr: {
      default: () => {
        return []
      }
    },
    'uniID': {
      required: true
    }
  },
  setup(props, { emit }) {

    async function getData (defaultParams = defaultRelevenceParams) {
      let { relevence, selectedAddr, pageIndex } = defaultParams
      let searchRequest = selectedAddr.length > 0 ? {
        get: {
          登記地址近似公司: {
            "relevence": relevence,
            "selected_addr": selectedAddr,
            "page-index": pageIndex
          },
        },
        uniID: props.uniID
      } : {
        get: {
          登記地址近似公司: {
            "relevence": relevence,
            "page-index": pageIndex
          },
        },
        uniID: props.uniID
      }
      let result = await apis.companyGet(searchRequest)
        .then((data) => {
          // 全部的資料
          // commit('mutationCompanyData', data.payload)
          return data && data.payload && data.payload.登記地址近似公司 ? data.payload.登記地址近似公司 : undefined
        })
        .catch(e => {
          return undefined
        })
      return result
    }

    const isLoading = ref(false)

    const currentAddressSimilar = ref(props.登記地址近似公司)
    watch(() => props.登記地址近似公司, (newVal) => {
      currentAddressSimilar.value = newVal
    })
    // console.log('props.登記地址近似公司', props.登記地址近似公司)
    const currentTableData = computed(() => {
      return currentAddressSimilar.value.list.map((item) => {
        return {
          company_name: item.company_name,
          company_operate: item.company_operate,
          company_address: item.company_address,
          etax_matters_tideup: item.etax_matters_tideup.map((ele) => `${ele.title}(${ele.code})`),
          representative_name: item.representative_name
            ? '負責人：' + item.representative_name
            : '',
          shareholders: item.shareholders.map((ele) => `${ele.title}：${ele.name}`),
          managers: item.managers.map((ele) => `經理人：${ele.name}`),
          similar_shareholders: item.similar_shareholders.map((ele) => `${ele.title}：${ele.name}`)
        }
      })
    })
    const peopleStatObj = computed(() => {
      return currentAddressSimilar.value.cls
    })
    const returnCompanyLinkTo = (keyword, keywordType = 'scout') => {
      if (!keyword) return null
      return {
        name: 'list',
        query: { keyword, keywordType }
      }
    }
    const totalCount = computed(() => currentAddressSimilar.value.stat['total-count'])
    const syncPageIndex = computed({
      get: () => currentAddressSimilar.value.stat['page-index'],
      set: (value) => emit('update:pageIndex', value)
    })
    const onPageChange = async (value) => {
      syncPageIndex.value = value
      isLoading.value = true
      currentAddressSimilar.value = await getData({ pageIndex: value, relevence: resolutionLevel.value, selectedAddr: selectedAddr.value })
      isLoading.value = false
    }
    const collapseShowList = ref(['cls'])

    const resolutionLevel = ref(defaultRelevenceParams.relevence)
    const onResolutionLevelChange = async (value) => {
      resolutionLevel.value = value
      isLoading.value = true
      currentAddressSimilar.value = await getData({ pageIndex: syncPageIndex.value, relevence: value, selectedAddr: selectedAddr.value })
      isLoading.value = false
    }
    const selectedAddr = ref([])
    const selectedAddrOptions = computed(() => {
      return props.selectable_addr.map((item) => {
        return {
          label: item.num,
          value: item.num
        }
      })
    })
    // 初始時預設勾選門牌範圍
    // watch(() => props.selectable_addr, (newVal) => {
    //   selectedAddr.value = props.selectable_addr
    //     .filter(item => item.checked)
    //     .map(item => item.num)
    // }, { immediate: true })
    const onSelectedAddrChange = async (value) => {
      selectedAddr.value = value
      isLoading.value = true
      currentAddressSimilar.value = await getData({ pageIndex: syncPageIndex.value, relevence: resolutionLevel.value, selectedAddr: value })
      isLoading.value = false
    }


    return {
      isLoading,
      currentTableData,
      peopleStatObj,
      returnCompanyLinkTo,
      totalCount,
      syncPageIndex,
      onPageChange,
      collapseShowList,
      resolutionLevelOptions,
      resolutionLevel,
      onResolutionLevelChange,
      selectedAddr,
      selectedAddrOptions,
      onSelectedAddrChange,
    }
  }
})
</script>

<style lang="scss" scoped>
.toolbar {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}
.collapse {
  ::v-deep .el-collapse {
    max-width: 100%;
    background-color: #EFEFEF;
    padding-left: 1rem;
    padding-right: 1rem;
    .el-collapse-item__header, .el-collapse-item__wrap {
      background-color: #EFEFEF;
      margin-top: 0;
      border-radius: 0px;
      border: 0px;
      color: var(--color-black);
      font-size: 1rem;
    }
    .el-collapse-item__wrap {
      padding-top: 0.25rem;
      padding-bottom: 0.25rem; 
      .detail-card-toolbar-item {
        margin-top: 0.75rem;
        margin-bottom: 0.75rem;
      }
    }
    .el-collapse-item__content {
      padding-bottom: 0rem;
    }
  }
}
</style>